import * as React from 'react';
import Transition from 'react-transition-group/Transition';
import Blanket from '@atlaskit/blanket';
import type { SearchBarProps } from '../search-bar';
import type { SearchResultProps } from '../search-result';
import type { TransitionState } from 'view/styles/transitions';
import { ScrollLock } from 'view/common/scroll-lock';
import {
    SearchBar,
    SearchResult,
    SearchContent,
    Background,
    MODAL_FADE_TRANSITION_MS,
    FixedModal,
    FadeInSearchContents,
} from './styled';
import { FireScreenEventOnMount } from 'util/analytics/fire-screen-event';
import { MODAL_EXPAND_TRANSITION_MS } from '../styled';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { TRACK_EVENT_TYPE, OPERATIONAL_EVENT_TYPE } from '@atlassian/analytics-web-react';

type ChildProps = SearchBarProps & SearchResultProps & WithAnalyticsEventsProps;

export interface Props extends ChildProps {
    shown: boolean;
    fadeSearchBarInOut: boolean;
    portalId?: string;
}

interface State {
    autoFocusOnFirstItem: boolean;
}

export default class SearchPageModal extends React.Component<Props, State> {
    state: State = {
        autoFocusOnFirstItem: false,
    };

    resetAutoFocus() {
        this.setState({
            autoFocusOnFirstItem: false,
        });
    }

    onSubmit = () => {
        this.setState({
            autoFocusOnFirstItem: true,
        });
    };

    onFocus = () => {
        this.resetAutoFocus();
    };

    onChange = (term: string) => {
        this.resetAutoFocus();
        this.props.onChange(term, this.getAnalyticsData());
    };

    //Adding new portal and RT search success/failure analytics events as Operational event as mention here- https://hello.atlassian.net/wiki/spaces/TRELLO/pages/911176726/How%2BTo%2BReview%2Ba%2BGAS%2Bspec%2Band%2BPR
    getAnalyticsData = () => {
        const { createAnalyticsEvent, portalId } = this.props;
        return {
            analyticsSuccessEvent: {
                kb: createAnalyticsEvent?.({
                    action: portalId ? 'KBArticleSinglePortalSearch success' : 'KBArticleHomePageSearch success',
                    analyticsType: TRACK_EVENT_TYPE,
                    actionSubject: 'PortalSearch',
                }),
                portal: createAnalyticsEvent?.({
                    action: 'portalSearchSucceeded',
                    actionSubject: 'PortalSearch',
                    analyticsType: OPERATIONAL_EVENT_TYPE,
                    actionSubjectId: portalId ? 'singlePortal' : 'portalHomePage',
                }),
                requestType: createAnalyticsEvent?.({
                    action: 'requestTypeSearchSucceeded',
                    actionSubject: 'PortalSearch',
                    analyticsType: OPERATIONAL_EVENT_TYPE,
                    actionSubjectId: portalId ? 'singlePortal' : 'portalHomePage',
                }),
            },
            analyticsFailureEvent: {
                kb: createAnalyticsEvent?.({
                    action: portalId ? 'KBArticleSinglePortalSearch failed' : 'KBArticleHomePageSearch failed',
                    analyticsType: OPERATIONAL_EVENT_TYPE,
                }),
                portal: createAnalyticsEvent?.({
                    action: 'portalSearchFailed',
                    actionSubject: 'PortalSearch',
                    analyticsType: OPERATIONAL_EVENT_TYPE,
                    actionSubjectId: portalId ? 'singlePortal' : 'portalHomePage',
                }),
                requestType: createAnalyticsEvent?.({
                    action: 'requestTypeSearchFailed',
                    actionSubject: 'PortalSearch',
                    analyticsType: OPERATIONAL_EVENT_TYPE,
                    actionSubjectId: portalId ? 'singlePortal' : 'portalHomePage',
                }),
            },
        };
    };

    // Required to reset state when we close the modal. We can replace this with
    // getDerivedStateFromProps() when we upgrade to React 16.3.
    UNSAFE_componentWillUpdate(nextProps: Props) {
        if (!nextProps.shown && nextProps.shown !== this.props.shown) {
            this.setState({
                autoFocusOnFirstItem: false,
            });
        }
    }

    render() {
        const {
            shown,
            onRequestClose,
            term,
            queryTime,
            result,
            hasError,
            searchStartTime,
            isSearching,
            isSearchingPortals,
            isSearchingKb,
            isSearchingRequestTypes,
            fadeSearchBarInOut,
            portalId,
        } = this.props;

        const searchBar = (
            <SearchBar
                actionSubjectId="helpCenterSearch"
                onRequestClose={onRequestClose}
                resetSearch={this.props.resetSearch}
                onChange={this.onChange}
                onSubmit={this.onSubmit}
                onFocus={this.onFocus}
                term={term}
            />
        );

        return (
            <Transition in={shown} timeout={{ enter: 0, exit: MODAL_FADE_TRANSITION_MS }} mountOnEnter unmountOnExit>
                {(state: TransitionState) => (
                    <FixedModal
                        onRequestClose={onRequestClose}
                        appRoot={document.getElementById('root') as HTMLElement}
                    >
                        {shown && !fadeSearchBarInOut && searchBar}
                        <FadeInSearchContents state={state}>
                            {fadeSearchBarInOut && searchBar}
                            <SearchContent>
                                <Transition
                                    in={!!term && term.trim() !== ''}
                                    timeout={{ enter: 0, exit: MODAL_EXPAND_TRANSITION_MS }}
                                    mountOnEnter
                                    unmountOnExit
                                >
                                    {(transitionState: TransitionState) => (
                                        <>
                                            <Background state={transitionState} />

                                            <SearchResult
                                                data-test-id="search-result"
                                                result={result}
                                                isSearching={isSearching}
                                                isSearchingPortals={isSearchingPortals}
                                                isSearchingKb={isSearchingKb}
                                                isSearchingRequestTypes={isSearchingRequestTypes}
                                                hasError={hasError}
                                                term={term}
                                                queryTime={queryTime}
                                                state={transitionState}
                                                autoFocusOnFirstItem={this.state.autoFocusOnFirstItem}
                                                itemsFocusable={shown}
                                                isShown={shown}
                                                searchStartTime={searchStartTime}
                                                portalId={portalId}
                                            />
                                        </>
                                    )}
                                </Transition>
                            </SearchContent>

                            <FireScreenEventOnMount />
                            <ScrollLock />
                            <Blanket shouldAllowClickThrough={false} isTinted onBlanketClicked={onRequestClose} />
                        </FadeInSearchContents>
                    </FixedModal>
                )}
            </Transition>
        );
    }
}
